import React, { useState, useRef, useEffect, useCallback, useContext, useMemo } from 'react';
import SphereAvatar from '../../../molecules/sphere-avatar/sphere-avatar';
import UrgencyIcon from '../../../atoms/urgency/urgency';
import AtlasUrgencyPill from '../AtlasUrgencyPill/AtlasUrgencyPill';
import EditPill from '../../EditPill/EditPill';
import StatusIcon from '../../../atoms/status/status';
import { Toast } from 'primereact/toast';
import { Sphere } from 'types/global';
import { PermissionList } from 'types/permissions';
import { updateUrgency } from '@center-platform/spheres/sphere';
import './NodeLabel.scss';
import { useApiClientsOrNull } from '../../../../context/ApiClientsContext';
import { useMetaspaceEvents } from '../../../../hooks/useMetaspaceEvents';
import { useEventSubscribers } from '../../../../hooks/useEventSubscribers';
import { useTranslation } from 'localization';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '@center-pwa/store/store';
import { selectArcTemplate, fetchArcTemplate } from '@center-pwa/reducers/arcTemplatesReducer';
import { AtlasViewContext } from '../../../organisms/AtlasComponent/AtlasViewContext';
import { AtlasMultiUrgencyPillMolecule } from '../AtlasMultiUrgencyPill';
import StatusBadgeAtom from '../../../atoms/status-badge/StatusBadgeAtom';
import { useSphereStatus } from '../../../../hooks/useSphereStatus';
import MobileStatusDrawerMolecule from '../MobileStatusDrawer/MobileStatusDrawerMolecule';
import { useSchemaType } from '../../../../hooks/useSchemaType';

interface NodeLabelProps {
  sphere: Sphere;
  level: number;
  isParentNode: boolean;
  breadcrumbPath: string;
  onBreadcrumbClick?: (part: string, event: React.MouseEvent) => void;
  onNodeClick: () => void;
  charLimit: number;
  parentNode?: Sphere;
  fetchPermissionList: (
    sphereId: string,
    nodeLabel: string,
    permissions?: string[]
  ) => Promise<PermissionList | null>;
  notification?: { type: 'success' | 'error'; message: string; timer?: number };
  onNotificationDismiss?: (sphereId: string) => void;
  onShowAddSphere: (parentId?: Sphere, grandparentSphere?: Sphere) => void;
  onShowEditSphere: (
    sphere: Sphere,
    parentSphere: Sphere,
    viewMode: boolean
  ) => void;
  onDeactivateNode: (nodeId: string) => void;
  isExpandedByEdgeClick?: boolean;
  /** Whether this node's own subtree is expanded — carries `aria-expanded` here, on the label's interactive `role="button"`, not on the parent `<li>` (S6811: `listitem` does not support the attribute). */
  isTreeExpanded?: boolean;
  expandedSphereIds?: string[];
  onChatBubbleClick?: (sphereId: string) => void;
  isMobile?: boolean;
  mobileMolecules?: any[];
  organizationId?: string;
  showToolbar?: boolean; // Override for toolbar visibility (from metaspace showMetasphereToolbar setting)
  /** Show "Deploy approach" row-menu item. */
  showDeployApproach?: boolean;
  /** Called when the user selects "Deploy approach" from the row menu. */
  onShowDeployApproach?: (sphere: Sphere) => void;
}

interface DeactivateSphereResponse {
  success: boolean;
  message?: string;
}

const NodeLabel: React.FC<NodeLabelProps> = ({
  sphere,
  level,
  isParentNode,
  breadcrumbPath,
  onBreadcrumbClick,
  onNodeClick,
  charLimit,
  parentNode,
  fetchPermissionList,
  notification,
  onNotificationDismiss,
  onShowAddSphere,
  onShowEditSphere,
  onDeactivateNode,
  isExpandedByEdgeClick,
  isTreeExpanded,
  expandedSphereIds,
  onChatBubbleClick,
  isMobile = false,
  mobileMolecules = [],
  organizationId,
  showToolbar = true,
  showDeployApproach = false,
  onShowDeployApproach,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const apiClients = useApiClientsOrNull();
  const { emitChatEvent } = useMetaspaceEvents();
  const { hasSubscribers: hasChatSubscribers } = useEventSubscribers('CHAT_TRIGGERED');
  const { t } = useTranslation();
  const atlasConfigContext = useContext(AtlasViewContext);

  // Check if flow is expanded in properties (server-side expanded with expandFlow=true)
  const flowSphere: Sphere | null = sphere.properties?.flow && typeof sphere.properties.flow === 'object'
    ? (sphere.properties.flow as Sphere)
    : null;

  // Get flow template from Redux if flow sphere exists
  const getScalar = (x: any): string => Array.isArray(x) ? String(x[0]) : String(x);
  const templateName = flowSphere?.properties?.templateSystemName
    ? getScalar(flowSphere.properties.templateSystemName)
    : '';
  const flowTemplate = useSelector((state: RootState) =>
    templateName ? selectArcTemplate(state, templateName) : null
  );

  // Fetch flow template if needed
  useEffect(() => {
    if (templateName && !flowTemplate && apiClients?.schemaClient) {
      dispatch(fetchArcTemplate({ systemName: templateName, apiClient: apiClients.schemaClient }));
    }
  }, [templateName, flowTemplate, apiClients?.schemaClient, dispatch]);

  // All hooks must be called before any early returns (Rules of Hooks)
  const graphClient = apiClients?.graphClient;
  const atlasConfig = atlasConfigContext?.viewConfig;
  // Resolves an 'Aspect' mobile-detail row's kind (date vs. everything else) —
  // the mobile drawer has no other source of a property's schema type. Only
  // fetched on mobile, mirroring the createdBy/updatedBy fetches above.
  const mobileSchemaType = Array.isArray(sphere?.properties?.type)
    ? sphere.properties.type[0]
    : sphere?.properties?.type;
  const { schema: mobileDetailSchema } = useSchemaType(isMobile ? mobileSchemaType ?? null : null);
  const [isTruncated, setIsTruncated] = useState(true);
  const [isUrgencyPillOpen, setIsUrgencyPillOpen] = useState(false);
  const [isEditPillOpen, setIsEditPillOpen] = useState(false);
  const [isDeactivateConfirmOpen, setIsDeactivateConfirmOpen] =
    useState<boolean>(false);
  const [deactivateStatus, setDeactivateStatus] = useState<
    'idle' | 'success' | 'error'
  >('idle');
  // Determine if sphere has multiple perspective assignments
  const connectingPerspectives = sphere.connectingPerspectives || [];
  const hasMultiplePerspectives = connectingPerspectives.length > 1;

  // For single perspective, use first edge; for multiple, we'll use aggregated display
  const urgencyValue = Number(connectingPerspectives[0]?.urgency) || 2;
  const initialEdgeId = connectingPerspectives[0]?.edgeId;
  const [urgencyCode, setUrgencyCode] = useState<number>(urgencyValue);
  const [edgeId, setEdgeId] = useState<string | undefined>(initialEdgeId);
  const [isDetailsExpanded, setIsDetailsExpanded] = useState(false);
  const [isStatusDrawerVisible, setIsStatusDrawerVisible] = useState(false);

  // Fetch sphere status for mobile view
  const { status: statusValue, point: statusPoint } = useSphereStatus(isMobile ? sphere.id : undefined);

  // Calculate aggregated urgency (max across all perspectives) for display
  const aggregatedUrgency = useMemo(() => {
    if (connectingPerspectives.length === 0) return 2;
    return Math.max(...connectingPerspectives.map((p) => Number(p.urgency) || 2));
  }, [connectingPerspectives]);

  const glyph = sphere.glyph ?? 'default-avatar';
  const glyphColor = sphere.glyphColor;
  const sphereId = sphere.id;
  const image = Array.isArray(sphere.properties.imageId)
    ? sphere.properties.imageId[0] || null
    : sphere.properties.imageId || null;

  // --- References for outside click detection ---
  const urgencyPillRef = useRef<HTMLDivElement | null>(null);
  const editPillRef = useRef<HTMLDivElement | null>(null);
  const toastRef = useRef<Toast>(null);
  const breadcrumbRef = useRef<HTMLSpanElement | null>(null);
  const nodeLabelRef = useRef<HTMLDivElement | null>(null);

  const [shouldTruncateBreadcrumbs, setShouldTruncateBreadcrumbs] =
    useState(false);

  const [localNotification, setLocalNotification] = useState<{
    type: 'success' | 'error' | 'info';
    message: string;
    timer?: number;
  } | null>(null);
  const BREADCRUMB_WIDTH_LIMIT = 400;

  useEffect(() => {
    if (breadcrumbRef.current) {
      const breadcrumbWidth = breadcrumbRef.current.offsetWidth;
      const nodeLabelWidth = nodeLabelRef.current?.offsetWidth;
      setShouldTruncateBreadcrumbs(breadcrumbWidth > BREADCRUMB_WIDTH_LIMIT);
    }
  }, [breadcrumbPath, isTruncated]);

  useEffect(() => {
    setUrgencyCode(urgencyValue);
    setEdgeId(initialEdgeId);
  }, [urgencyValue, initialEdgeId]);

  useEffect(() => {
    if (notification) {
      setLocalNotification({
        type: notification.type,
        message: notification.message,
        timer: notification.timer,
      });
    }
  }, [notification]);

  useEffect(() => {
    if (localNotification?.timer) {
      const timerId = setTimeout(() => {
        setLocalNotification(null);
        setDeactivateStatus('idle');
        // Notify parent to remove from nodeNotifications state
        // so it won't reappear on re-render
        if (onNotificationDismiss) {
          onNotificationDismiss(sphere.id);
        }
      }, localNotification.timer);

      return () => clearTimeout(timerId);
    }
  }, [localNotification, onNotificationDismiss, sphere.id]);
  
  // Toggle the UrgencyPill when clicking on the urgency icon
  const handleUrgencyIconClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsUrgencyPillOpen((prev) => !prev);
  };

  // Close EditPill when MetaspaceMenu (or any sibling popup) opens
  useEffect(() => {
    const handler = () => setIsEditPillOpen(false);
    document.addEventListener('atlas:close-edit-pills', handler);
    return () => document.removeEventListener('atlas:close-edit-pills', handler);
  }, []);

  // Toggle the EditPill when clicking on the edit icon
  const handleEditIconClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Close any open MetaspaceMenu popups before opening EditPill
    document.dispatchEvent(new CustomEvent('atlas:close-metaspace-menus'));
    setIsEditPillOpen((prev) => !prev);
  };

  // Hide the UrgencyPill if user clicks outside
  useEffect(() => {
    const handleDocClick = (e: MouseEvent) => {
      if (
        isUrgencyPillOpen &&
        urgencyPillRef.current &&
        !urgencyPillRef.current.contains(e.target as Node)
      ) {
        setIsUrgencyPillOpen(false);
      }
    };
    document.addEventListener('mousedown', handleDocClick);
    return () => {
      document.removeEventListener('mousedown', handleDocClick);
    };
  }, [isUrgencyPillOpen]);

  // Hide the EditPill if user clicks outside
  useEffect(() => {
    const handleDocClick = (e: MouseEvent) => {
      if (
        isEditPillOpen &&
        editPillRef.current &&
        !editPillRef.current.contains(e.target as Node)
      ) {
        setIsEditPillOpen(false);
      }
    };
    document.addEventListener('mousedown', handleDocClick, true);
    return () => {
      document.removeEventListener('mousedown', handleDocClick, true);
    };
  }, [isEditPillOpen]);

  // Function to handle user selecting an urgency item (e.g., "Urgent", "High", etc.)
  // If it fails, revert to the previous value and show a notification.
  const handleUrgencyOptionClick = useCallback(
    async (urgency: string, edgeId: string) => {
      if (!edgeId || !graphClient) return;

      // Optimistically update urgency value
      const oldUrgency = urgencyCode;
      const oldEdgeId = edgeId;
      setUrgencyCode(parseInt(urgency, 10));
      setIsUrgencyPillOpen(false);

      try {
        // Use the refactored updateUrgency helper with apiClient
        const response = await updateUrgency(graphClient, {
          edgeId,
          urgencyCode: urgency,
        });
        const { newEdgeId } = response;
        setEdgeId(newEdgeId);
      } catch (error) {
        console.error('Error updating urgency:', error);

        // Revert local state if API call fails
        setUrgencyCode(oldUrgency);
        setEdgeId(oldEdgeId);

        // Show a notification for the failure
        setLocalNotification({
          type: 'error',
          message: t('atlas.errors.updateUrgencyFailed'),
          timer: 3000,
        });
      }
    },
    [graphClient, urgencyCode, edgeId]
  );

  // Define deactivateSphereAPI as a callback using apiClient
  const deactivateSphereAPI = useCallback(
    async (sphereId: string): Promise<void> => {
      if (!graphClient) {
        throw new Error('API client not available');
      }
      try {
        await graphClient.delete<DeactivateSphereResponse>(
          `/spheres/${sphereId}/delete`
        );
      } catch (error: any) {
        console.error('Error in deactivateSphereAPI:', error);
        throw error;
      }
    },
    [graphClient]
  );

  // Function to handle edit actions from EditPill
  const handleEditClick = async (edit: string) => {
    switch (edit) {
      case '0':
        // Open dialog in view mode (read-only)
        if (!parentNode) {
          console.warn('Parent Sphere not found for viewing');
          return;
        }
        onShowEditSphere(sphere, parentNode, true);
        break;
      case '1':
        // Open the deactivate confirmation prompt
        setIsDeactivateConfirmOpen(true);
        break;
      case '2':
        if (!parentNode) {
          console.warn('Parent Sphere not found for editing');
          return;
        }
        onShowEditSphere(sphere, parentNode, false);
        break;
      case '3':
        onShowAddSphere(sphere, parentNode);
        break;
      case '4':
        // Open conversation/chat
        handleChatBubbleClick();
        break;
      case '5':
        // Deploy approach — opens AddSphereDialogOrganism in Deploy mode pre-targeted to this sphere
        if (onShowDeployApproach) {
          onShowDeployApproach(sphere);
        }
        break;
      default:
        console.error('Invalid edit value:', edit);
    }
    setIsEditPillOpen(false);
  };

  // Function to handle deactivate action
  const handleDeactivate = async () => {
    setIsDeactivateConfirmOpen(false);
    setDeactivateStatus('idle');
    try {
      await deactivateSphereAPI(sphere.id);
      toastRef.current?.show({
        severity: 'success',
        summary: t('profile.success'),
        detail: t('atlas.sphere.deactivateSuccess'),
        life: 3000,
      });
      setDeactivateStatus('success');
      // Delay the deletion to allow the toast to show
      setTimeout(() => {
        onDeactivateNode(sphere.id);
      }, 3000);
    } catch (error: any) {
      console.error('Error deactivating sphere:', error);
      setDeactivateStatus('error');
      setLocalNotification({
        type: 'error',
        message: t('atlas.errors.deactivateFailed'),
        timer: 3000,
      });
    }
  };

  // Function to handle canceling deactivate action
  const handleCancelDeactivate = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsDeactivateConfirmOpen(false);
    setDeactivateStatus('idle');
  };

  const handleChatBubbleClick = (e?: React.MouseEvent) => {
    e?.stopPropagation();

    console.log('🔵 [Atlas] Chat bubble clicked for sphere:', sphere.id, sphere.properties?.name);

    // Emit the chat event through the event system
    emitChatEvent(sphere.id, {
      sphereName: sphere.properties?.name || t('common.unknownSphere'),
      level,
      parentSphereId: parentNode?.id,
      triggerSource: 'atlas_node_label',
    });

    console.log('📤 [Atlas] Chat event emitted');

    // Still call the callback for backward compatibility (if needed)
    if (onChatBubbleClick) {
      onChatBubbleClick(sphere.id);
    }
  };

  const handleExpandToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsDetailsExpanded(!isDetailsExpanded);
  };

  const handleStatusClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsStatusDrawerVisible(true);
  };

  const handleStatusDrawerHide = () => {
    setIsStatusDrawerVisible(false);
  };

  const handleStatusDeployed = () => {
    // Refresh the status after deployment
    // The useSphereStatus hook will automatically refetch
  };

  const handleStatusRefreshed = (newStatus: any) => {
    // Status has been refreshed, the hook will update automatically
    console.log('[NodeLabel] Status refreshed:', newStatus);
  };

  const handleConfigureFactors = (sphereId: string) => {
    // Open sphere editor to configure status factors
    if (parentNode) {
      onShowEditSphere(sphere, parentNode, false);
    }
  };

  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return t('common.notSet');

    try {
      const date = new Date(dateString);
      // Format with both date and time in user's local timezone
      return date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
    } catch {
      return dateString;
    }
  };



  const getAssignedPerspectiveSphere = (perspectiveSystemName: string): Sphere | null => {
    if (!sphere.assignedPerspectives) return null;

    const assignedPerspective = sphere.assignedPerspectives.find(
      (p) => p.perspectiveProperties.system_name === perspectiveSystemName
    );

    if (!assignedPerspective?.assigned || assignedPerspective.assigned.length === 0) {
      return null;
    }

    return assignedPerspective.assigned[0].sphere || null;
  };

  const renderMobileDetails = (): JSX.Element[] => {
    if (!mobileMolecules || mobileMolecules.length === 0) {
      return [];
    }

    const detailElements: JSX.Element[] = [];

    // Define perspective display order: Center, Parent, Responsible
    const perspectiveOrder = ['center', 'parent', 'responsible'];

    // Sort molecules to ensure perspectives appear in correct order
    const sortedMolecules = [...mobileMolecules].sort((a, b) => {
      // Only sort PerspectiveSelector molecules
      if (a.type === 'PerspectiveSelector' && b.type === 'PerspectiveSelector') {
        const aSystemName = a.moleculeProps?.perspectiveSystemName || '';
        const bSystemName = b.moleculeProps?.perspectiveSystemName || '';

        // Extract perspective type (center_responsible -> responsible, etc.)
        const aType = aSystemName.replace('center_', '').toLowerCase();
        const bType = bSystemName.replace('center_', '').toLowerCase();

        const aIndex = perspectiveOrder.indexOf(aType);
        const bIndex = perspectiveOrder.indexOf(bType);

        // If both are in the order array, sort by position
        if (aIndex !== -1 && bIndex !== -1) {
          return aIndex - bIndex;
        }

        // If only one is in the order array, prioritize it
        if (aIndex !== -1) return -1;
        if (bIndex !== -1) return 1;
      }

      // Keep original order for non-perspective molecules
      return 0;
    });

    sortedMolecules.forEach((molecule: any, index: number) => {
      const { moleculeProps = {} } = molecule;

      switch (molecule.type) {
        case 'NodeDetailsButton':
          // Description
          if (sphere.properties.description) {
            detailElements.push(
              <div key="description" className="detail-row detail-full">
                <span className="detail-label">
                  <i className="pi pi-align-left"></i>
                  {' '}
                  {t('common.description')}
                </span>
                <div
                  className="detail-value detail-description"
                  dangerouslySetInnerHTML={{ __html: sphere.properties.description }}
                />
              </div>
            );
          }
          break;

        case 'PerspectiveSelector': {
          // Dynamic perspective (center_responsible, center_accountable, etc.)
          const { perspectiveSystemName } = moleculeProps;
          const assignedSphere = getAssignedPerspectiveSphere(perspectiveSystemName);

          if (assignedSphere) {
            // Get display name from system_name
            const displayName = perspectiveSystemName
              .replace('center_', '')
              .replaceAll('_', ' ')
              .replaceAll(/\b\w/g, (l: string) => l.toUpperCase());

            // Get image ID from sphere properties
            const imageId = assignedSphere.properties?.image_id || assignedSphere.properties?.imageId;

            detailElements.push(
              <div key={`persp-${perspectiveSystemName}`} className="detail-row">
                <span className="detail-label">
                  <i className="perspective-icon"></i>
                  {displayName}
                </span>
                <div className="detail-value perspective-value">
                  <SphereAvatar
                    glyph={assignedSphere.glyph}
                    glyphColor={assignedSphere.glyphColor}
                    sphereId={assignedSphere.id}
                    sphereName={assignedSphere.properties?.name}
                    imageId={imageId}
                    size="small"
                    className="perspective-sphere-avatar"
                    decorative
                  />
                  <span className="perspective-name">{assignedSphere.properties?.name}</span>
                </div>
              </div>
            );
          }
          break;
        }

        case 'Aspect': {
          // Formerly two separate types — AtlasDateSelector (date fields like
          // startDate/targetDate) and AtlasTextColumn (text fields like timeSpent) —
          // collapsed onto 'Aspect' (EAINT-866 Phase 3). The bound aspect's schema
          // kind picks which of the two renderings below applies; a text-kind aspect
          // renders identically to the old AtlasTextColumn row.
          const { aspect } = moleculeProps;
          const aspectValue = aspect ? sphere.properties[aspect] : undefined;

          if (aspect && aspectValue) {
            const propertyDef = mobileDetailSchema?.properties.find(
              (p) => p.name === aspect
            );
            const isDateAspect = propertyDef?.type === 'date' || propertyDef?.type === 'datetime';
            const displayName = aspect
              .replaceAll(/([A-Z])/g, ' $1')
              .replace(/^./, (str: string) => str.toUpperCase())
              .trim();

            detailElements.push(
              isDateAspect ? (
                <div key={`aspect-${aspect}`} className="detail-row detail-row-nested">
                  <span className="detail-label">{displayName}</span>
                  <div className="detail-value">
                    {formatDate(aspectValue)}
                  </div>
                </div>
              ) : (
                <div key={`aspect-${aspect}`} className="detail-row">
                  <span className="detail-label">
                    <i className="pi pi-clock"></i>
                    {displayName}
                  </span>
                  <div className="detail-value">
                    {aspectValue}
                  </div>
                </div>
              )
            );
          }
          break;
        }

        case 'ArcStageSelector': {
          // Arc stage selector - display current stage if sphere has an expanded arc
          if (Array.isArray(flowSphere?.stages)) {
            // Find the active stage
            const activeStage = flowSphere.stages.find((stage: any) => stage.status === 'active');

            if (activeStage) {
              // Get stage definition from flow template
              const stageId = activeStage.stageDefinitionId || '';
              const stageDef = flowTemplate?.definition?.stages?.find((s: any) => s.id === stageId);

              // Get stage icon (same logic as StageDropdownAtom)
              const getStageIcon = (stageDef?: any, status?: string) => {
                // Priority 1: Use icon from stage definition if specified
                if (stageDef?.icon) {
                  return `pi pi-${stageDef.icon}`;
                }

                // Priority 2: Fallback to status-based icons
                if (status === 'completed') return 'pi pi-check-circle';
                if (status === 'active') return 'pi pi-play-circle';
                return 'pi pi-circle';
              };

              const stageIcon = getStageIcon(stageDef, activeStage.status || 'not_started');

              // Get stage name from template with localization, or format the stageId as fallback
              const stageName = stageDef
                ? t(stageDef.localizationKey, stageDef.fallbackName)
                : stageId
                    .split('_')
                    .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                    .join(' ');

              detailElements.push(
                <div key="arc-stage" className="detail-row">
                  <span className="detail-label">
                    <i className="pi pi-flag"></i>
                    {' '}
                    {t('atlas.stage', { defaultValue: 'Stage' })}
                  </span>
                  <div className="detail-value">
                    <span className="stage-chip">
                      <i className={`stage-icon ${stageIcon}`}></i>
                      {stageName || t('arc.ui.no_active_stage')}
                    </span>
                  </div>
                </div>
              );
            }
          }
          break;
        }

        default:
          break;
      }
    });

    return detailElements;
  };

  // Function to generate a path from ancestors like in UserSphere
  const generatePathFromAncestors = (ancestors: Sphere[]): string => {
    const pathArray: string[] = [];
    const traverseAncestors = (node: Sphere) => {
      if (node.properties?.name) {
        pathArray.push(node.properties.name);
      }
      if (node.ancestors && node.ancestors.length > 0) {
        traverseAncestors(node.ancestors[0]);
      }
    };
    if (ancestors.length > 0) {
      traverseAncestors(ancestors[0]);
    }
    return pathArray.join(' / ');
  };

  const ancestorsPath = generatePathFromAncestors(sphere.ancestors || []);
  const parentPath = ancestorsPath
    ? `${ancestorsPath} / ${breadcrumbPath}`
    : sphere.properties.name;
  const nodeLabel = ancestorsPath
    ? `${ancestorsPath} / ${sphere.properties.name}`
    : sphere.properties.name;
  const label = `${'... '.repeat(level)}${nodeLabel ?? t('common.unknownSphere')}`;
  const lockFocus = atlasConfig?.lockFocus ?? false;
  // Absent means shown: only an explicit `false` hides the indicator, so views stored
  // before the flag existed keep rendering it. Gates the icon AND its pill together —
  // the pill is the icon's only trigger, so leaving it mounted would be dead markup.
  const showUrgency = atlasConfig?.showUrgency !== false;

  const handleLabelClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Check if focus is locked in atlas configuration
    if (!lockFocus) {
      onNodeClick();
    }
  };

  const toggleTruncation = (
    event: React.MouseEvent | React.KeyboardEvent
  ): void => {
    event.stopPropagation();
    setIsTruncated(!isTruncated);
  };

  // Mobile hybrid breadcrumb: show only current sphere for long paths (2+ segments)
  const renderMobileBreadcrumb = (path: string) => {
    const parts = path.split(' / ');
    const isLongPath = parts.length > 1;

    if (isLongPath) {
      // Long path: Show parent avatar + separator + current sphere with avatar
      const currentSphereName = sphere.properties?.name || parts[parts.length - 1];
      const parentSphereName = parts[parts.length - 2]; // Get parent sphere name
      const currentImageId = sphere.properties?.image_id || sphere.properties?.imageId;

      // Find parent sphere from ancestors or use parentNode prop
      const findParentSphere = (sphereNode: Sphere, parentName: string): Sphere | null => {
        // First check if the parentNode prop matches
        if (parentNode?.properties?.name === parentName) {
          return parentNode;
        }

        // Otherwise search in ancestors
        const queue = [...(sphereNode.ancestors || [])];
        while (queue.length > 0) {
          const current = queue.shift();
          if (current?.properties?.name === parentName) return current;
          if (current?.ancestors) {
            queue.push(...current.ancestors);
          }
        }
        return null;
      };

      const parentSphereData = findParentSphere(sphere, parentSphereName);
      const parentImageId = parentSphereData?.properties?.image_id || parentSphereData?.properties?.imageId;

      return (
        <div className="mobile-breadcrumb-current">
          {/* Parent Sphere Avatar (clickable to navigate back) */}
          <button
            type="button"
            className="mobile-parent-avatar-button"
            onClick={(e) => {
              e.stopPropagation();
              if (onBreadcrumbClick && parentSphereName) {
                onBreadcrumbClick(parentSphereName, e);
              }
            }}
            aria-label={t('atlas.backTo', { defaultValue: 'Back to {{parent}}', parent: parentSphereName })}
            title={t('atlas.backTo', { defaultValue: 'Back to {{parent}}', parent: parentSphereName })}
          >
            <SphereAvatar
              glyph={parentSphereData?.glyph}
              glyphColor={parentSphereData?.glyphColor}
              sphereId={parentSphereData?.id}
              sphereName={parentSphereName}
              imageId={parentImageId}
              size="small"
              className="parent-sphere-avatar"
            />
          </button>

          {/* Separator */}
          <span className="sphere-seprater"> / </span>

          {/* Current Sphere Avatar + Name */}
          <SphereAvatar
            glyph={sphere.glyph}
            glyphColor={sphere.glyphColor}
            sphereId={sphere.id}
            sphereName={currentSphereName}
            imageId={currentImageId}
            size="small"
            className="current-sphere-avatar"
            decorative
          />
          <span className="current-sphere-name" title={currentSphereName}>
            {currentSphereName}
          </span>
        </div>
      );
    }

    // Short path: Render full breadcrumb
    return renderBreadcrumbs(path);
  };

  const renderBreadcrumbs = (path: string) => {
    const parts = path.split(' / ');
  
    return parts.map((part, index) => {
      const findMatchingSphere = (node: Sphere, part: string): Sphere | null => {
        const queue = [node];
        while (queue.length > 0) {
          const current = queue.shift();
          if (current?.properties.name === part) return current;
          if (current?.ancestors) {
        queue.push(...current.ancestors);
          }
        }
        return null;
      };
  
      const matchingSphere = findMatchingSphere(sphere, part);
  
      return (
        <span
          key={`${part}-${index}`}
          style={{ display: 'inline-flex', alignItems: 'center' }}
        >
          <a
            href="#"
            role="button"
            tabIndex={lockFocus ? -1 : 0}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!lockFocus) {
                onBreadcrumbClick?.(part, e);
              }
            }}
            onKeyDown={(e) => {
              if ((e.key === 'Enter' || e.key === ' ') && !lockFocus) {
                e.preventDefault();
                onBreadcrumbClick?.(part, e as any);
              }
            }}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', cursor: lockFocus ? 'default' : 'pointer' }}
          >
            <SphereAvatar
              glyph={matchingSphere?.glyph}
              glyphColor={matchingSphere?.glyphColor}
              sphereId={matchingSphere?.id}
              imageId={matchingSphere?.properties?.imageId}
              className="breadcrumb-avatar"
              size="small"
              decorative
            />
            <span
              className={`sphere-breadcrumb ${
                shouldTruncateBreadcrumbs ? 'truncate' : ''
              }`}
              title={part}
            >
              {part}
            </span>
          </a>
          {index < parts.length - 1 && (
            <span className="sphere-seprater"> / </span>
          )}
        </span>
      );
    });
  };
  
  const renderTruncatedBreadcrumbs = (path: string) => {
    const parts = path.split(' / ');
    const maxVisibleParts = 3;

    const findMatchingSphere = (node: Sphere, part: string): Sphere | null => {
      if (node.properties.name === part) return node;
      for (const ancestor of node.ancestors || []) {
        const found = findMatchingSphere(ancestor, part);
        if (found) return found;
      }
      return null;
    };

    const visibleParts = parts.slice(-maxVisibleParts);
    const hiddenParts = parts.slice(0, parts.length - maxVisibleParts);

    const renderPart = (part: string, index: number, total: number) => {
      const matchingSphere = findMatchingSphere(sphere, part);
      return (
        <span
          key={`${part}-${index}`}
          style={{ display: 'inline-flex', alignItems: 'center' }}
        >
          <a
            href="#"
            role="button"
            tabIndex={lockFocus ? -1 : 0}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!lockFocus) {
                onBreadcrumbClick?.(part, e);
              }
            }}
            onKeyDown={(e) => {
              if ((e.key === 'Enter' || e.key === ' ') && !lockFocus) {
                e.preventDefault();
                onBreadcrumbClick?.(part, e as any);
              }
            }}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', cursor: lockFocus ? 'default' : 'pointer' }}
          >
            <SphereAvatar
              glyph={matchingSphere?.glyph}
              glyphColor={matchingSphere?.glyphColor}
              sphereId={matchingSphere?.id}
              imageId={matchingSphere?.properties?.imageId}
              className="breadcrumb-avatar"
              size="small"
              decorative
            />
            <span className="sphere-breadcrumb" title={part}>
              {part}
            </span>
          </a>
          <span className="sphere-seprater">{index < total - 1 && ' / '}</span>
        </span>
      );
    };

    return (
      <button
        className="btn-nobg"
        tabIndex={0}
        onMouseLeave={(e) => {
          e.stopPropagation();
          setIsTruncated(true);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setIsTruncated(true);
          }
        }}
      >
        {shouldTruncateBreadcrumbs && isTruncated && (
            <button
            className="breadcrumb-link mt-1 breadcrumb-hover-wrapper btn-link"
            style={{ cursor: 'pointer', background: 'none', border: 'none', padding: 0 }}
            aria-label="Show full path"
            onMouseEnter={(e) => {
              e.stopPropagation();
              setIsTruncated(false);
            }}
            onClick={(e) => {
              e.stopPropagation();
              setIsTruncated(false);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setIsTruncated(false);
              }
            }}
            >
            ... /
            </button>
        )}

        {/* Show hidden parts only when expanded */}
        {!isTruncated &&
          hiddenParts.map((part, index) =>
            renderPart(part, index, parts.length)
          )}

        {/* Always show last 3 parts */}
        {visibleParts.map((part, index) =>
          renderPart(part, hiddenParts.length + index, parts.length)
        )}
      </button>
    );
  };

  // Helper function to determine which breadcrumb renderer to use - extracted to avoid nested ternary
  const getBreadcrumbRenderer = (path: string) => {
    if (isMobile) {
      return renderMobileBreadcrumb(path);
    }
    if (shouldTruncateBreadcrumbs) {
      return renderTruncatedBreadcrumbs(path);
    }
    return renderBreadcrumbs(path);
  };

  const renderValue = (value: string | object | undefined) => {
    if (value === undefined) return '';
    if (typeof value === 'object') return JSON.stringify(value);
    return value;
  };

  // Early return if API clients not available (must be after all hooks)
  if (!apiClients) {
    return null;
  }

  return (
    <>
      <Toast ref={toastRef} />
      <div
        className={`node-label ${isTruncated ? '' : 'expanded'} ${lockFocus ? 'lock-focus' : ''}`}
        ref={nodeLabelRef}
        onClick={handleLabelClick}
        role="button"
        tabIndex={0}
        aria-expanded={isTreeExpanded}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleLabelClick(e as unknown as React.MouseEvent);
          }
        }}
        style={lockFocus ? { cursor: 'default' } : undefined}
      >
        {/* Expand/Collapse Icon - Only show on mobile */}
        {isMobile && (
          <button
            type="button"
            onClick={handleExpandToggle}
            className={`mobile-expand-icon ${isDetailsExpanded ? 'expanded' : ''}`}
            aria-label={isDetailsExpanded ? t('atlas.details.collapse') : t('atlas.details.expand')}
            aria-expanded={isDetailsExpanded}
          >
            <i className={`pi ${isDetailsExpanded ? 'pi-chevron-down' : 'pi-chevron-right'}`}></i>
          </button>
        )}

        {/* Urgency Icon - shows aggregated urgency for multiple perspectives */}
        {showUrgency && (
          <span
            role="button"
            tabIndex={0}
            onClick={handleUrgencyIconClick}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleUrgencyIconClick(e as unknown as React.MouseEvent);
              }
            }}
            className="urgency-icon"
          >
            <UrgencyIcon urgencyValue={hasMultiplePerspectives ? aggregatedUrgency : urgencyCode} />
          </span>
        )}

        {/* UrgencyPill component - conditionally render based on perspective count */}
        {showUrgency && (hasMultiplePerspectives ? (
          <AtlasMultiUrgencyPillMolecule
            isOpen={isUrgencyPillOpen}
            onClose={() => setIsUrgencyPillOpen(false)}
            onUrgencyChange={handleUrgencyOptionClick}
            connectingPerspectives={connectingPerspectives}
            parentNode={parentNode}
            fetchPermissionList={fetchPermissionList}
            sphereType={sphere.properties.type || ''}
          />
        ) : (
          <AtlasUrgencyPill
            isOpen={isUrgencyPillOpen}
            onClose={() => setIsUrgencyPillOpen(false)}
            onUrgencyChange={handleUrgencyOptionClick}
            urgencyValue={urgencyValue}
            parentNode={parentNode}
            edgeId={edgeId}
            fetchPermissionList={fetchPermissionList}
          />
        ))}

        {/* Sphere Avatar */}
        <SphereAvatar
          glyph={glyph}
          glyphColor={glyphColor}
          sphereId={sphereId}
          imageId={image}
          size="small"
          className="sphere-avatar-icon"
        />

        {/* Breadcrumb Path or Labels */}
        {isParentNode ? (
          <span className="breadcrumb-path" ref={breadcrumbRef}>
            {getBreadcrumbRenderer(parentPath ?? '')}
          </span>
        ) : (
          <>
            {typeof label === 'string' &&
              label.split(/(\.\.\.)/g).map((part, index) => (
                <React.Fragment key={`${part}-${index}`}>
                  {part === '...' ? (
                    <div className="atlas-Ellipsis">
                      <span>...</span>
                    </div>
                  ) : (
                    <label
                    className={`${
                      expandedSphereIds?.includes(sphere.id) ? 'bold-node-label' : ''
                    }`}
                  >
                    {renderValue(part.trim()) ?? ''}
                  </label>
                  
                  )}
                </React.Fragment>
              ))}
          </>
        )}

        {/* Edit Pill Wrapper - Ensure it's the last child */}
        <div className="edit-pill-wrapper" ref={editPillRef}>
          {/* Chat Bubble Icon - Only show if there are subscribers for CHAT_TRIGGERED event */}
          {hasChatSubscribers && (
            <span
              className="chat-bubble-icon-wrapper"
              role="button"
              tabIndex={0}
              onClick={handleChatBubbleClick}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleChatBubbleClick(e as unknown as React.MouseEvent<HTMLSpanElement>);
                }
              }}
            >
              <i className="chat-bubble-icon text-primary"></i>
            </span>
          )}
          <span
            role="button"
            tabIndex={0}
            onClick={handleEditIconClick}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleEditIconClick(e as unknown as React.MouseEvent);
              }
            }}
            className="row-control"
          >
            <i className={`pi ellipsis-circle-icon edit-pill-icon`}></i>
          </span>            
          {/* Deactivate Confirmation Prompt */}
          {isDeactivateConfirmOpen && (
            <div
              className="deactivate-confirmation"
              onClick={(e) => e.stopPropagation()}
            >
              <span>{t('atlas.sphere.deactivateConfirm')}</span>
              <button onClick={handleDeactivate} className="confirm-button">
                {t('common.confirm')}
              </button>
              <button
                onClick={handleCancelDeactivate}
                className="cancel-button"
              >
                {t('common.cancel')}
              </button>
            </div>
          )}

          {/* Edit Pill Component */}
          <EditPill
            isOpen={isEditPillOpen}
            onClose={() => setIsEditPillOpen(false)}
            onEditChange={handleEditClick}
            showDeactivate={sphere.properties.type !== 'center_organization'}
            showConversation={isMobile && showToolbar && hasChatSubscribers}
            showDeployApproach={showDeployApproach}
            parentNode={parentNode}
            sphere={sphere}
            fetchPermissionList={fetchPermissionList}
          />
        </div>

        {/* Notification */}
        {localNotification && (
          <div
            className={`node-label-notification ${localNotification.type}`}
            role="alert"
            aria-live="assertive"
          >
            <i
              className={`notification-icon ${localNotification.type}-icon mr-2`}
            >
              {/* SVG Icons Based on Notification Type */}
              {localNotification.type === 'success' && (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  fill="currentColor"
                  viewBox="0 0 16 16"
                >
                  <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM7 11.414L3.707 8.121 2.293 9.536 7 14.243l9-9-1.414-1.414L7 11.414z" />
                </svg>
              )}
              {localNotification.type === 'error' && (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  fill="currentColor"
                  viewBox="0 0 16 16"
                >
                  <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM8 4.993a.905.905 0 0 0-.9.9v2.706a.905.905 0 1 0 1.81 0V5.893A.905.905 0 0 0 8 4.993zM8 11a1 1 0 1 0 0 2 1 1 0 0 0 0-2z" />
                </svg>
              )}
              {localNotification.type === 'info' && (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  fill="currentColor"
                  viewBox="0 0 16 16"
                >
                  <path d="M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1zM8 13a.905.905 0 0 1-.9-.9V8.9a.905.905 0 1 1 1.81 0v3.201A.905.905 0 0 1 8 13zM8 4a1 1 0 1 1 0 2 1 1 0 0 1 0-2z" />
                </svg>
              )}
            </i>
            <span>{localNotification.message}</span>
          </div>
        )}
      </div>

      {/* Mobile Details Panel */}
      {isMobile && isDetailsExpanded && (
        <div className="mobile-details-panel">
          <div className="mobile-details-content">
            {/* Status - Always show first - Clickable to open drawer */}
            <div className="detail-row detail-row-clickable" onClick={handleStatusClick}>
              <span className="detail-label">
                <i className="pi pi-info-circle"></i>
                {' '}
                {t('common.status')}
              </span>
              <div className="detail-value detail-value-with-chevron">
                <StatusBadgeAtom
                  status={statusValue || 'unknown'}
                  size={20}
                />
                {statusValue && statusValue !== 'unknown' && (
                  <span className="status-text">
                    {t(`statusPopover.statusValues.${statusValue}`, statusValue)}
                  </span>
                )}
                <i className="pi pi-chevron-right"></i>
              </div>
            </div>

            {/* Dynamic content based on molecules */}
            {renderMobileDetails()}
          </div>
        </div>
      )}

      {/* Mobile Status Drawer */}
      {isMobile && (
        <MobileStatusDrawerMolecule
          visible={isStatusDrawerVisible}
          onHide={handleStatusDrawerHide}
          sphereId={sphere.id}
          sphereName={sphere.properties?.name || t('common.unknownSphere')}
          schemaType={sphere.properties?.type}
          status={statusPoint || undefined}
          onStatusDeployed={handleStatusDeployed}
          onStatusRefreshed={handleStatusRefreshed}
          onConfigureFactors={handleConfigureFactors}
          fetchPermissionList={fetchPermissionList}
        />
      )}
    </>
  );
};

export default NodeLabel;